import { DayType, ZmanimData } from '../types';
import { isErevYomTovDate, isHoshanaRabba } from './hebrewSeason';
import { getDayLabels } from './minyanDay';

/** Day types that only override specific prayers; others fall back to weekday. */
export const MODIFIER_DAY_TYPES: DayType[] = ['roshChodesh', 'sheniHamishi'];

/** Specific-occasion Yom Tov day types — exclude generic 'yomTov' at detection time (Part A above), fall back to it in scheduleResolver.ts when no specific entry exists for that prayer type. */
export const YOM_TOV_DAY_TYPES: DayType[] = [
  'roshHashana',
  'sukkotI',
  'simchatTorah',
  'pesachI',
  'shviiShelPesach',
  'shavuot',
];

/**
 * All day-type labels that apply to this date (a Friday that is also Erev Yom Tov
 * yields both erevYomTov and erevShabbos). Used for schedule matching.
 * Order follows detectDayType priority.
 *
 * Exclusions (matching only — badge/primary order is unchanged):
 * - Yom Kippur is not also yomTov (generic YT entries must not match).
 * - Rosh Hashana / any chag is not also roshChodesh (RH is the only RC+YT overlap).
 */
export function getApplicableDayTypes(zmanim: ZmanimData): DayType[] {
  const types: DayType[] = [];
  if (zmanim.isYomKippur) types.push('yomKippur');
  if (zmanim.isTishaBav) types.push('tishaBav');
  if (zmanim.isFastDay) types.push('fastMinor');
  // YK is flagged as CHAG in zmanim, but it is not a generic yomTov for matching.
  // Specific-occasion tags EXCLUDE the generic 'yomTov' tag (not additive —
  // "the specific overrides the general", Yoel's explicit decision
  // 2026-09-29). A generic yomTov-tagged entry still applies on every one
  // of these occasions via the yomTov-family fallback in
  // scheduleResolver.ts, UNLESS a more specific entry exists for that
  // exact occasion + prayer type.
  if (zmanim.isYomTov && !zmanim.isYomKippur) {
    const name = zmanim.yomTovName || '';
    // Substring match, not exact equality — MyZmanim's yomTovName format
    // is unverified (fetchZmanim.ts isn't deployed yet), so this stays
    // tolerant. Verified against Hebcal directly (this app's live
    // fallback today) for Israel, running @hebcal/core against this
    // project's own node_modules: "Rosh Hashana 5787" / "Rosh Hashana II"
    // (both contain "Rosh Hashana"), "Pesach I", "Pesach VII", "Sukkot I",
    // "Shmini Atzeret" (this is ALSO what Hebcal calls Simchat Torah —
    // Israel's single combined day; Hebcal never emits a separate
    // "Simchat Torah" string), "Shavuot".
    const specificYomTov: DayType[] = [];
    if (name.includes('Rosh Hashana')) specificYomTov.push('roshHashana');
    if (name.includes('Pesach I') && !name.includes('Pesach VII')) specificYomTov.push('pesachI');
    if (name.includes('Pesach VII')) specificYomTov.push('shviiShelPesach');
    if (name.includes('Sukkot I')) specificYomTov.push('sukkotI');
    if (name.includes('Shmini Atzeret')) specificYomTov.push('simchatTorah');
    if (name.includes('Shavuot')) specificYomTov.push('shavuot');

    if (specificYomTov.length > 0) {
      types.push(...specificYomTov);
    } else {
      // A Yom Tov day with no specific tag matched (shouldn't happen
      // against the current Israel Hebcal holiday set, but stay safe
      // rather than silently showing nothing) — fall back to the
      // generic tag itself.
      types.push('yomTov');
    }
  }
  if (zmanim.isCholHaMoed) types.push('cholHaMoed');
  // Child of Chol HaMoed: a Hoshana Rabba Shacharis replaces the Chol HaMoed one (scheduleResolver).
  if (zmanim.isCholHaMoed && isHoshanaRabba(zmanim.date)) types.push('hoshanaRabba');

  // From the calendar date, not the provider's flag (see isErevYomTovDate).
  if (isErevYomTovDate(zmanim.date)) types.push('erevYomTov');
  if (zmanim.isShabbos) types.push('shabbos');
  if (zmanim.isErevShabbos) types.push('erevShabbos');
  // No other Yom Tov coincides with Rosh Chodesh; RH is the only case this hits.
  if (
    zmanim.isRoshChodesh &&
    !zmanim.isYomTov &&
    !zmanim.isYomKippur
  ) {
    types.push('roshChodesh');
  }
  if (types.length === 0) {
    // Parse YYYY-MM-DD as local components (Asia/Jerusalem) — avoid UTC Date parsing.
    const [year, month, day] = zmanim.date.split('-').map(Number);
    const dayOfWeek = new Date(year, month - 1, day).getDay(); // 0=Sun ... 6=Sat
    if (dayOfWeek === 1 || dayOfWeek === 4) {
      types.push('sheniHamishi');
    } else {
      types.push('weekday');
    }
  }
  return types;
}

/** The main label for a date (minyanim): the first of getDayLabels() (minyanDay.ts). */
export function detectDayType(zmanim: ZmanimData): DayType {
  return getDayLabels(zmanim.date)[0];
}

/** Get display label for a DayType in English or Hebrew */
export function getDayTypeLabel(dayType: DayType, lang: 'en' | 'he'): string {
  const labels: Record<DayType, { en: string; he: string }> = {
    weekday:     { en: 'Weekday',             he: 'ימות החול' },
    erevShabbos: { en: 'Erev Shabbos',        he: 'ערב שבת' },
    shabbos:     { en: 'Shabbos',             he: 'שבת' },
    erevYomTov:  { en: 'Erev Yom Tov',        he: 'ערב יום טוב' },
    yomTov:      { en: 'Yom Tov',             he: 'יום טוב' },
    roshHashana: { en: 'Rosh Hashana',       he: 'ראש השנה' },
    sukkotI:     { en: 'First Day of Sukkot', he: 'ראשון של סוכות' },
    // UI says "Simchat Torah"; detection matches Hebcal's "Shmini Atzeret" (Israel combined day).
    simchatTorah:    { en: 'Simchat Torah',     he: 'שמחת תורה' },
    pesachI:     { en: 'First Day of Pesach', he: 'ראשון של פסח' },
    shviiShelPesach: { en: 'Shvii Shel Pesach', he: 'שביעי של פסח' },
    shavuot:         { en: 'Shavuot',           he: 'שבועות' },
    cholHaMoed:  { en: 'Chol HaMoed',         he: 'חול המועד' },
    hoshanaRabba: { en: 'Hoshana Rabba',      he: 'הושענא רבה' },

    roshChodesh: { en: 'Rosh Chodesh',        he: 'ראש חודש' },
    sheniHamishi: { en: 'Mondays & Thursdays', he: 'שני וחמישי' },
    fastMinor:   { en: 'Fast Day',            he: 'תענית' },
    tishaBav:    { en: "Tisha B'Av",          he: 'תשעה באב' },
    yomKippur:   { en: 'Yom Kippur',          he: 'יום כיפור' },
    erevYomKippur: { en: 'Erev Yom Kippur',   he: 'ערב יום כיפור' },
    erevTishaBav: { en: "Erev Tisha B'Av",    he: 'ערב תשעה באב' },
    chanukah: { en: 'Chanukah', he: 'חנוכה' },
    purim: { en: 'Purim', he: 'פורים' },
    aseretYemeiTeshuva: { en: 'Aseret Yemei Teshuva', he: 'עשרת ימי תשובה' },
    behab: { en: 'BeHaB', he: 'בה״ב' },
    yomTovFriday: { en: 'Yom Tov on Friday', he: 'יום טוב שחל ביום שישי' },
    roshHashanaFriday: { en: 'Rosh Hashana day 2 on Friday', he: 'ב׳ דראש השנה ביום שישי' },
    chanukahFriday: { en: 'Friday of Chanukah', he: 'ערב שבת חנוכה' },
    fastFriday: { en: 'Fast on Friday', he: 'תענית ביום שישי' },
    purimFriday: { en: 'Purim on Friday', he: 'פורים ביום שישי' },
    chanukahShabbos: { en: 'Shabbos Chanukah', he: 'שבת חנוכה' },
    beinHazmanim: { en: 'Bein Hazmanim', he: 'בין הזמנים' },
    selichotFirstNight: { en: 'Selichos: first night', he: 'סליחות: לילה ראשון' },
    selichotBeforeRH: { en: 'Selichos before Rosh Hashana', he: 'סליחות לפני ראש השנה' },
    erevRoshHashana: { en: 'Erev Rosh Hashana', he: 'ערב ראש השנה' },
    tzomGedalia: { en: 'Tzom Gedalia', he: 'צום גדליה' },
  };
  return labels[dayType][lang];
}
